import { Info } from '@mui/icons-material';
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { getIntegrationStatusByInternalName } from 'lib/api/integrations';
import { getActiveMoneyReports } from 'lib/api/reports';
import { downloadAEC } from 'lib/api/reSell/factoring';
import { getSelectedCertificate } from 'lib/api/SIICertificates';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import { DocumentProfileContext } from 'lib/context/DocumentProfileProvider';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions, {
  CustomButtonProps,
} from 'lib/designSystem/HeaderWithActions';
import TableWithPagination from 'lib/designSystem/TableWithPagination';
import { showAlert, validatePermissions } from 'lib/functions/utils';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import DebtProjector from './components/DebtProjector';
import PortfolioFlow from './components/PortfolioFlow';
import ProvisionBuilder from './components/ProvisionBuilder';

type ActiveMoneyTab = 'cartera' | 'flujoCartera';

const ActiveMoney: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ActiveMoneyTab>('cartera');
  const [flujoCarteraButtons, setFlujoCarteraButtons] = useState<
    CustomButtonProps[]
  >([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingAEC, setLoadingAEC] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const [openProvisionBuilder, setOpenProvisionBuilder] = useState(false);
  const { onOpenDocumentProfile } = useContext(DocumentProfileContext);
  const [dataType, setDataType] = useState('active');
  const [businessIdentifier, setBusinessIdentifier] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [stakeholderIdentifier, setStakeholderIdentifier] = useState('');
  const [stakeholderName, setStakeholderName] = useState('');
  const [documentType, setDocumentType] = useState('all');
  const [requestId, setRequestId] = useState('');
  const [folio, setFolio] = useState('');
  const [executive, setExecutive] = useState<
    string | { label: string; value: string } | null
  >('');
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.auth.user);
  const PAGE_SIZE = 10;
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });
  const [selectedCertificate, setSelectedCertificate] = useState<object | null>(
    null,
  );
  const [hasCessionIntegration, setHasCessionIntegration] = useState(false);
  const [startOperationDate, setStartOperationDate] = useState<string | null>(
    null,
  );
  const [endOperationDate, setEndOperationDate] = useState<string | null>(null);
  const [startPaidAt, setStartPaidAt] = useState<string | null>(null);
  const [endPaidAt, setEndPaidAt] = useState<string | null>(null);
  const [startPaymentDate, setStartPaymentDate] = useState<string | null>(null);
  const [endPaymentDate, setEndPaymentDate] = useState<string | null>(null);
  const generateQuery = ({
    page,
    pageSize,
    exportComplete,
  }: {
    page: number;
    pageSize: number;
    exportComplete: boolean;
  }) => {
    let query = '';
    if (documentType) query += `requestType=${documentType}&`;
    if (folio) query += `folio=${folio}&`;
    if (dataType) query += `dataType=${dataType}&`;
    if (businessIdentifier)
      query += `businessIdentifier=${businessIdentifier}&`;
    if (businessName)
      query += `businessName=${businessName.replace('&', '%26')}&`;
    if (stakeholderIdentifier)
      query += `stakeholderIdentifier=${stakeholderIdentifier}&`;
    if (stakeholderName) query += `stakeholderName=${stakeholderName}&`;
    if (requestId) query += `requestId=${requestId}&`;
    if (executive && executive !== 'all') {
      query += `executive=${executive}&`;
    }
    if (page) query += `page=${page}&pageSize=${pageSize}&`;
    if (exportComplete) query += `exportComplete=true&`;
    if (startOperationDate)
      query += `startOperationDate=${startOperationDate}&`;
    if (endOperationDate) query += `endOperationDate=${endOperationDate}&`;
    if (startPaidAt) query += `startPaidAt=${startPaidAt}&`;
    if (endPaidAt) query += `endPaidAt=${endPaidAt}&`;
    if (startPaymentDate) query += `startPaymentDate=${startPaymentDate}&`;
    if (endPaymentDate) query += `endPaymentDate=${endPaymentDate}&`;
    return query;
  };

  const downloadFile = (signedUrl: string, index: number) => {
    setTimeout(() => {
      const link = document.createElement('a');
      link.href = signedUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }, index * 250);
  };

  const handleDownloadMultipleAECs = async (ids: string[]) => {
    setLoadingAEC(true);
    const erroredIds = [];
    try {
      const response = await downloadAEC(dispatch, ids);
      let i = 0;
      for (const documentAEC of response) {
        if (documentAEC.xml) {
          downloadFile(documentAEC.xml, i);
          i += 1;
          showAlert(dispatch, 'success', 'AEC descargado correctamente');
        } else {
          erroredIds.push(
            ` \n${documentAEC.xmlFileName}: ${documentAEC.errorMessage}`,
          );
        }
      }
      showAlert(dispatch, 'success', 'AECs descargados correctamente');
    } catch (err) {
      console.error(err);
      showAlert(dispatch, 'error', 'Error inesperado al descargar los AECs');
    }
    if (erroredIds.length > 0) {
      showAlert(
        dispatch,
        'error',
        `AECs no descargados: ${erroredIds.join(', ')}`,
      );
    }
    setLoadingAEC(false);
  };

  const handleDownloadAEC = async (id: string) => {
    setLoadingAEC(true);
    try {
      const response = await downloadAEC(dispatch, [id]);
      const documentAEC = response[0];
      if (documentAEC.xml) {
        downloadFile(documentAEC.xml, documentAEC.xmlFileName);
        showAlert(dispatch, 'success', 'AEC descargado correctamente');
      } else {
        showAlert(
          dispatch,
          'error',
          'El archivo AEC no se encuentra disponible',
        );
      }
    } catch (error) {
      showAlert(dispatch, 'error', 'Error inesperado');
      console.error(error);
    }
    setLoadingAEC(false);
  };

  const handleDownloadAECObject = {
    key: 'downloadAEC',
    label: '',
    handleAction: handleDownloadAEC,
    icon: <Typography fontWeight={800}>AEC</Typography>,
    keyDisabled: 'canShowAec',
  };

  const setUpCertificates = async () => {
    try {
      const response = await getSelectedCertificate(dispatch);
      setSelectedCertificate(response);
    } catch (error) {
      console.error(error);
    }
  };

  const checkCessionIntegration = async () => {
    try {
      const response = await getIntegrationStatusByInternalName(
        dispatch,
        'recesion',
      );
      setHasCessionIntegration(response.status);
    } catch (error) {
      console.error(error);
    }
  };

  const setUp = async () => {
    setLoading(true);
    await setUpCertificates();
    await checkCessionIntegration();
    await setUpData({ page: 0, pageSize: PAGE_SIZE });
    setLoading(false);
  };

  const canShowMultipleDownloadAEC = () => {
    if (loadingAEC) return false;
    const documentsSelected = data.filter((document: any) =>
      selectedDocuments.includes(document.id),
    );
    if (documentsSelected.length === 0) {
      return false;
    }
    if (!documentsSelected.every((document: any) => document.canShowAec)) {
      return false;
    }
    return true;
  };

  const canShowDownloadAEC = hasCessionIntegration && selectedCertificate;

  const setUpData = async ({
    page = 0,
    pageSize = PAGE_SIZE,
    exportComplete = false,
  }: {
    page: number;
    pageSize: number;
    exportComplete?: boolean;
  }): Promise<{
    data: any[];
    pagination: {
      page: number;
      pageSize: number;
      total: number;
      totalPages: number;
    };
  }> => {
    const query = generateQuery({ page, pageSize, exportComplete });
    try {
      const response = await getActiveMoneyReports(dispatch, query);
      if (!exportComplete) {
        setData(response.data);
        setPagination(response.pagination);
      }
      return response;
    } catch (error) {
      console.error(error);
      return {
        data: [],
        pagination: { page, pageSize, total: 0, totalPages: 0 },
      };
    }
  };
  useEffect(() => {
    void setUp();
  }, []);

  const filters: Filter[] = [
    {
      label: 'Fecha de solicitud',
      type: 'date',
      value: startOperationDate,
      setter: setStartOperationDate,
      dateKey: 'startDate',
      dateType: 'request',
    },
    {
      label: 'Fecha de solicitud',
      type: 'date',
      value: endOperationDate,
      setter: setEndOperationDate,
      dateKey: 'endDate',
      dateType: 'request',
    },
    {
      label: 'Fecha de pago',
      type: 'date',
      value: startPaidAt,
      setter: setStartPaidAt,
      dateKey: 'startPaidDate',
      dateType: 'payment',
    },
    {
      label: 'Fecha de pago',
      type: 'date',
      value: endPaidAt,
      setter: setEndPaidAt,
      dateKey: 'endPaidDate',
      dateType: 'payment',
    },
    {
      label: 'Fecha de vencimiento',
      type: 'date',
      value: startPaymentDate,
      setter: setStartPaymentDate,
      dateKey: 'startPaymentDate',
      dateType: 'payment',
    },
    {
      label: 'Fecha de vencimiento',
      type: 'date',
      value: endPaymentDate,
      setter: setEndPaymentDate,
      dateKey: 'endPaymentDate',
      dateType: 'payment',
    },
    {
      label: 'Tipo de cartera',
      type: 'select',
      value: dataType,
      setter: setDataType,
      options: [
        { label: 'Activa', value: 'active' },
        { label: 'Completada', value: 'completed' },
        { label: 'Todas', value: 'all' },
      ],
    },
    {
      label: 'Tipo de documento',
      type: 'select',
      value: documentType,
      setter: setDocumentType,
      options: [
        { label: 'Todos', value: 'all' },
        { label: 'Factoring', value: 'factoring' },
        { label: 'Crédito', value: 'credit' },
        { label: 'Confirming', value: 'confirming' },
      ],
    },
    {
      label: 'Nombre cliente',
      type: 'autocomplete',
      value: businessName,
      key: 'businessName',
      setter: setBusinessName,
    },
    {
      label: 'Rut emisor',
      type: 'text',
      value: businessIdentifier,
      setter: setBusinessIdentifier,
    },
    {
      label: 'Nombre deudor',
      type: 'autocomplete',
      value: stakeholderName,
      key: 'stakeholderName',
      setter: setStakeholderName,
    },
    {
      label: 'Rut contraparte',
      type: 'text',
      value: stakeholderIdentifier,
      setter: setStakeholderIdentifier,
    },
    ...(validatePermissions(user, 'businesses_read_all')
      ? [
          {
            label: 'Ejecutivo',
            type: 'autocomplete' as const,
            value: executive,
            key: 'userName',
            setter: setExecutive,
          },
        ]
      : []),
    {
      label: 'Id de solicitud',
      type: 'text',
      value: requestId,
      setter: setRequestId,
    },
    {
      label: 'Folio',
      type: 'text',
      value: folio,
      setter: setFolio,
    },
  ];

  const triggerSearch = async () => {
    setLoading(true);
    await setUpData({ page: 0, pageSize: PAGE_SIZE });
    setLoading(false);
  };

  const handleInfoClick = (id: string) => {
    onOpenDocumentProfile(id);
  };

  const multipleButton: CustomButtonProps = {
    action: () => handleDownloadMultipleAECs(selectedDocuments),
    color: 'primary',
    name: 'Descargar AECs',
    disabled: !canShowMultipleDownloadAEC(),
    loading: loadingAEC,
  };

  const getCarteraButtons = (): CustomButtonProps[] => [
    ...(canShowDownloadAEC ? [multipleButton] : []),
    {
      action: () => setOpen(true),
      name: 'Proyectar mora',
      color: 'primary',
      disabled: selectedDocuments.length === 0 ? true : false,
    },
    {
      action: () => {},
      color: 'primary',
      customButton: (
        <GlobalSearcher
          filters={filters}
          triggerSearch={triggerSearch}
          dispatch={dispatch}
          disableSearch={loading}
        />
      ),
    },
  ];

  const getFlujoCarteraButtons = (): CustomButtonProps[] => {
    return flujoCarteraButtons;
  };

  const buttons: CustomButtonProps[] =
    activeTab === 'cartera' ? getCarteraButtons() : getFlujoCarteraButtons();

  if (loading) return <CenteredCircularProgress type="layout" />;
  return (
    <div>
      <HeaderWithActions
        buttons={buttons}
        variant="body1"
        customTitle={
          <Box>
            <ToggleButtonGroup
              value={activeTab}
              color="primary"
              exclusive
              onChange={(_, newValue) => {
                if (newValue !== null) {
                  setActiveTab(newValue);
                }
              }}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  minWidth: '140px',
                },
              }}
            >
              <ToggleButton value="cartera">Cartera</ToggleButton>
              <ToggleButton value="flujoCartera">
                Cartera Retroactiva
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        }
      />
      {activeTab === 'cartera' && (
        <TableWithPagination
          dataHeaders={[
            ...(canShowDownloadAEC ? [handleDownloadAECObject] : []),
            {
              key: ' ',
              label: '',
              handleAction: handleInfoClick,
              icon: <Info fontSize="small" />,
            },
            { key: 'businessName', label: 'Nombre cliente' },
            { key: 'businessIdentifier', label: 'Rut cliente', type: 'report' },
            { key: 'stakeholderName', label: 'Nombre deudor' },
            { key: 'stakeholderIdentifier', label: 'Rut deudor' },
            { key: 'operationId', label: 'Id solicitud', type: 'numeric-id' },
            { key: 'operationType', label: 'Tipo solicitud' },
            { key: 'operationDate', label: 'Fecha solicitud', type: 'date' },
            { key: 'documentType', label: 'Tipo documento' },
            { key: 'issuedDate', label: 'Emisión', type: 'eight-days-date' },
            {
              key: 'documentNumber',
              label: 'Folio',
            },
            { key: 'invoiceAmount', label: 'Monto original', type: 'money' },
            { key: 'amount', label: 'Monto documento', type: 'money' },
            {
              key: 'advance',
              label: 'Financiamiento',
              type: 'percentage-soft',
            },
            {
              key: 'rate',
              label: 'Tasa',
              type: 'percentage-soft',
            },
            {
              key: 'priceDifference',
              label: 'Diferencia de precio',
              type: 'money',
            },
            {
              key: 'anticipatedAmount',
              label: 'Monto anticipado',
              type: 'money',
            },
            { key: 'dueDate', label: 'Vencimiento', type: 'date' },
            {
              key: 'originalDueDate',
              label: 'Vencimiento original',
              type: 'date',
            },
            { key: 'operationDays', label: 'Días operación' },
            { key: 'documentState', label: 'Estado documento' },
            { key: 'siiDescription', label: 'Descripción SII' },
            { key: 'cessionStatus', label: 'Estado cesión' },
            {
              key: 'lastConciliationDate',
              label: 'Fecha último pago',
              type: 'date',
            },
            { key: 'lastConciliationPaidBy', label: 'Último pagador' },
            { key: 'conciliationAmount', label: 'Abono', type: 'money' },
            {
              key: 'documentRemainingAmount',
              label: 'Saldo documento',
              type: 'money',
            },
            {
              key: 'documentRemainingAmountNet',
              label: 'Saldo financiado',
              type: 'money',
            },
            { key: 'arrearAmount', label: 'Mora', type: 'money' },
            {
              key: 'totalRemainingNet',
              label: 'Saldo financiado + mora',
              type: 'money',
            },
            {
              key: 'creditNoteAmount',
              label: 'Monto nota crédito',
              type: 'money',
            },
            { key: 'arrearDays', label: 'Días mora' },
            { key: 'arrearAmount', label: 'Interés mora', type: 'money' },
            {
              key: 'surplusTotal',
              label: 'Excedentes retenidos',
              type: 'money',
            },
            {
              key: 'surplusEffective',
              label: 'Excedentes liberados',
              type: 'money',
            },
            { key: 'lastCollectionManagementOption', label: 'S. Cobranza' },
            { key: 'lastCollectionManagementStatus', label: 'E. Cobranza' },
            {
              key: 'lastCollectionManagementDescription',
              label: 'O. Cobranza',
            },
            {
              key: 'operationCost',
              label: 'Comisión solicitud',
              type: 'money',
            },
            {
              key: 'operationCostIVA',
              label: 'IVA comisión solicitud',
              type: 'money',
            },
            {
              key: 'operationExpenses',
              label: 'Gastos solicitud',
              type: 'money',
            },
            { key: 'collectorName', label: 'Ejecutivo Cobranza' },
            { key: 'fundType', label: 'Fondo' },
            { key: 'stillInResell', label: 'Estado Fondo', type: 'boolean' },
            { key: 'cancelDate', label: 'Rechazo', type: 'date' },
            { key: 'confirmDate', label: 'Acuse recibo', type: 'date' },
            {
              key: 'commercialExecutiveAssignedName',
              label: 'Ejecutivo comercial asignado',
            },
            {
              key: 'commercialExecutivePortfolioName',
              label: 'Ejecutivo comercial cartera',
            },
            { key: 'executiveManagerName', label: 'Manager' },
            { key: 'dailyAccrual', label: 'Devengo diario', type: 'money' },
            { key: 'economicSector', label: 'Sector económico' },
            { key: 'resellDate', label: 'Fecha fondo', type: 'date' },
            { key: 'resellRate', label: 'Tasa venta', type: 'percentage-soft' },
            { key: 'fundCostTotal', label: 'Costo total fondo', type: 'money' },
          ]}
          data={data}
          selected={selectedDocuments}
          setSelected={setSelectedDocuments}
          rowsPerPageDefault={100}
          loading={loading}
          searchData={setUpData}
          exportToFile="xlsx"
          exportComplete
          setLoading={setLoading}
          pagination={pagination}
          setPagination={setPagination}
          setData={setData}
          lastElement
        />
      )}
      <div style={{ display: activeTab === 'flujoCartera' ? 'block' : 'none' }}>
        <PortfolioFlow
          onButtonsReady={setFlujoCarteraButtons}
          isActive={activeTab === 'flujoCartera'}
        />
      </div>
      {open && (
        <DebtProjector
          open={open}
          documents={data.filter((document: any) =>
            selectedDocuments.includes(document.id),
          )}
          setOpen={setOpen}
        />
      )}
      <ProvisionBuilder
        open={openProvisionBuilder}
        setOpen={setOpenProvisionBuilder}
      />
    </div>
  );
};

export default ActiveMoney;
